import React, { memo, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _map from 'lodash/map';
import _findIndex from 'lodash/findIndex';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../assets/logo.png';
import { useOnClickOutside } from '../../custom-hooks/useClickOutside';
import { IAppContextStore, useAppContext } from '../../context/app-context';
import { filterItems } from '../../helpers/contentful-helper';
import HeaderMenuLink from './header-menu-link';
import { headerLinks } from './header.constants';
import { HeaderMenuItem } from './header.types';
import { Entry } from 'contentful';
import { IFooterSection } from '../footer/footer.types';
import { HamburgerMenuIcon } from '../icons';

const Header: React.FC = () => {
  const { rawContentFul }: IAppContextStore = useAppContext();
  const footerSection: Entry<IFooterSection> = useMemo(() => 
    filterItems(rawContentFul, 'newHomepageFooterSection')?.[0] as Entry<IFooterSection>
  , [rawContentFul]);
  const [openNav, setOpenNav] = useState(false);
  const [showMobileSearchInput, setShowMobileSearchInput] = useState(false);
  const menuArea: RefObject<HTMLDivElement> = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const location = useLocation();
  const navigate = useNavigate();
  const currentMenuIndex = useMemo(() => {
    if (location.pathname === '/') {
      return 0;
    }

    return _findIndex(headerLinks, (item: HeaderMenuItem) => location.pathname.startsWith(item.path), 1);
  }, [location]);
  //@ts-ignore

  useOnClickOutside(menuArea, () => setOpenNav(false));

  useEffect(() => {
    if (openNav === true) {
      document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    } else {
      document.getElementsByTagName('body')[0].style.overflow = 'auto';
    }

    return () => {
      document.getElementsByTagName('body')[0].style.overflow = 'auto';
    };
  }, [openNav]);

  const onClickAMenuLink = useCallback((item: HeaderMenuItem) => navigate(item.path), [navigate]);
  const searchInputMobileRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>() as RefObject<HTMLDivElement>;
  const searchInputMobile: RefObject<HTMLInputElement> = useRef<HTMLInputElement>() as RefObject<HTMLInputElement>;
  const menuWrapper: RefObject<HTMLInputElement> = useRef<HTMLInputElement>() as RefObject<HTMLInputElement>;
  useOnClickOutside(searchInputMobileRef, () => setShowMobileSearchInput(false));

  useEffect(() => {
    if (showMobileSearchInput === true) {
      searchInputMobile.current?.focus();
    }
  }, [showMobileSearchInput, searchInputMobile]);

  return (
    <>
      <div
        ref={searchInputMobileRef}
        className={
          `relative flex justify-center pt-26px pb-26px sm:pt-45px
          sm:pb-23px lg:pt-30px lg:pb-29px items-center sm:border-0
          lg:border-b px-4 ${
            currentMenuIndex === 0 ? 'lg:border-none' : 'border-jm-gray-content'
          }`
        }>
        <div className='flex items-center justify-between gap-4 w-full'>
          <div ref={menuWrapper} className='relative'>
            <div className='flex items-center'>
              <a href='/' className='block relative cursor-pointer z-10'>
                <img className='object-cover object-center h-[30px] md:h-11 lg:h-14' src={Logo} alt='SoundTool' />
              </a>
            </div>
          </div>

          <div className={`hidden lg:flex gap-4 xl:gap-10 ${currentMenuIndex === 0 ? 'text-white' : ''}`} >
            {
              _map(headerLinks, (item: HeaderMenuItem, index: number) => (
                <HeaderMenuLink
                  key={index}
                  item={item}
                  isActive={index === currentMenuIndex}
                  onClick={() => onClickAMenuLink(item)}
                />
              ))
            }
          </div>
          <div className='lg:hidden cursor-pointer' onClick={() => setOpenNav(!openNav)}>
            <HamburgerMenuIcon customClassName={`transition-transform duration-500 ${openNav ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </div>

      <div className={
        `transition-all flex justify-end z-10 duration-500 fixed top-0 left-0
        bg-jm-slide-background h-screen bg-opacity-60 opacity-100 w-full ${
          openNav ? '' : 'translate-x-[100%]'
        }`
      }>
        <div
          ref={menuArea}
          className='w-9/12 sm:w-4/12 bg-jm-nav-gray opacity-100 h-screen overflow-hidden flex flex-col text-right'>
          <div className='px-5 py-10 bg-jm-white flex flex-col space-y-3'>
            {
              _map(headerLinks, (item: HeaderMenuItem, index: number) => (
                <HeaderMenuLink key={index} item={item} isActive={index === currentMenuIndex}  onClick={() => {
                  setOpenNav(false);
                  onClickAMenuLink(item)
                }}  />
              ))
            }
          </div>
          <div className='flex-auto flex flex-col justify-end items-end p-5 pb-7 text-14'>
            <div>
              {footerSection?.fields?.copyright || `Copyright © ${new Date().getFullYear()} SoundTools. All Rights Reserved.`}
            </div>
          </div>
        </div>
      </div>
    </>
  )
};

export default memo(Header);
